import { INearbyPlace } from '../../components/NearbyPlaces/NearbyPlacesSection';
import { DISTANCE_UNITS } from '../../constants/constants';
import {
  GetFishingWaterNearestPlace_fishingWater_nearestPlace,
  GetFishingWaterNearestPlace_fishingWater_nearestPlace_nearbyPlaces_edges_node,
  GetPlaceNearbyPlaces_entry_nearbyPlaces_edges_node,
} from '../../interfaces/graphql';
import { getDistance } from '../location/getDistance';
import { getPlaceUrl } from '../urls';

interface IProps {
  distanceUnit: (typeof DISTANCE_UNITS)[keyof typeof DISTANCE_UNITS];
  nearbyPlaces: (
    | GetFishingWaterNearestPlace_fishingWater_nearestPlace_nearbyPlaces_edges_node
    | GetPlaceNearbyPlaces_entry_nearbyPlaces_edges_node
    | GetFishingWaterNearestPlace_fishingWater_nearestPlace
  )[];
  mainLatitude: number;
  mainLongitude: number;
}

/**
 * Retrieves a list of nearby places sorted by distance from a main location.
 *
 * @param {IProps} props - The properties for the function.
 * @param {(
 *   | GetFishingWaterNearestPlace_fishingWater_nearestPlace_nearbyPlaces_edges_node
 *   | GetPlaceNearbyPlaces_entry_nearbyPlaces_edges_node
 *   | GetFishingWaterNearestPlace_fishingWater_nearestPlace
 * )[]} props.nearbyPlaces - An array of nearby places to be processed.
 * @param {(typeof DISTANCE_UNITS)[keyof typeof DISTANCE_UNITS]} props.distanceUnit - The unit of distance measurement.
 * @param {number} props.mainLatitude - The latitude of the main location.
 * @param {number} props.mainLongitude - The longitude of the main location.
 *
 * @returns {INearbyPlace[]} An array of nearby places with their names, distances, and URLs, sorted by distance.
 */
export const getNearbyPlacesByDistance = ({
  nearbyPlaces,
  distanceUnit,
  mainLatitude,
  mainLongitude,
}: IProps): INearbyPlace[] => {
  return nearbyPlaces
    .flatMap(({ country, name, region, slug, longitude, latitude }) => {
      const url =
        country.id && region.id && slug ? getPlaceUrl(country.id, region.id, slug) : undefined;
      const distance =
        longitude && latitude
          ? getDistance(
              {
                latitude: mainLatitude,
                longitude: mainLongitude,
              },
              { longitude: longitude, latitude: latitude },
              distanceUnit,
            )
          : undefined;
      if (!name || !distance || !url) {
        return [];
      }
      return {
        name,
        distance,
        url,
      };
    })
    .sort((a, b) => {
      if (a.distance && b.distance) {
        return a.distance - b.distance;
      }
      return 0;
    });
};
